/**
 * Pure utility functions for video recording.
 * No side effects, no DOM access — all browser APIs are injected via arguments.
 */

/**
 * Format seconds into "M:SS" display string.
 * @param {number} totalSeconds
 * @returns {string}
 */
export const formatTime = (totalSeconds) => {
  const mins = Math.floor(totalSeconds / 60);
  const secs = Math.floor(totalSeconds % 60);
  return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
};

/**
 * Format seconds into a short "{N}s" display string.
 * @param {number} totalSeconds
 * @returns {string}
 */
export const formatTimeShort = (totalSeconds) => `${Math.floor(totalSeconds)}s`;

/**
 * Find the first MIME type from `candidates` that the browser supports.
 * @param {string[]} candidates — ordered MIME type strings
 * @param {function} isSupported — predicate (defaults to MediaRecorder.isTypeSupported)
 * @returns {string} matched MIME or ""
 */
export const getSupportedMime = (candidates, isSupported) =>
  candidates.find(isSupported) ?? "";

/**
 * Detect Mobile Safari from a user-agent string.
 * @param {string} ua — navigator.userAgent
 * @returns {boolean}
 */
export const detectMobileSafari = (ua) => {
  if (!ua) return false;
  const isSafari = ua.includes("Safari") && !ua.includes("Chrome");
  const isMobile = ua.includes("Mobile") || ua.includes("iPhone");
  return isSafari && isMobile;
};

/**
 * Validate a video file's duration by loading metadata.
 * Returns a Promise that resolves with the file or rejects with an Error.
 *
 * @param {File} file
 * @param {{ maxSeconds?: number, minSeconds?: number }} options
 * @returns {Promise<File>}
 */
export const validateFileDuration = (
  file,
  { maxSeconds = Infinity, minSeconds = 1 } = {},
) =>
  new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      const { duration } = video;
      if (duration < minSeconds) {
        reject(
          new Error(
            `Video must be at least ${minSeconds} second${
              minSeconds === 1 ? "" : "s"
            }`,
          ),
        );
      } else if (duration > maxSeconds) {
        const label =
          maxSeconds >= 60
            ? `${Math.floor(maxSeconds / 60)} minute${
                Math.floor(maxSeconds / 60) === 1 ? "" : "s"
              }`
            : `${maxSeconds} seconds`;
        reject(new Error(`Video must be under ${label}`));
      } else {
        resolve(file);
      }
    };
    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error("Unable to read video file"));
    };
    video.src = URL.createObjectURL(file);
  });

/**
 * Work around the Chrome bug where recorded webm blobs report
 * `duration === Infinity`. Seeks to MAX_SAFE_INTEGER then back to 0,
 * resolving when the video element has a finite duration.
 *
 * @param {HTMLVideoElement} videoEl
 * @returns {Promise<void>}
 */
export const applyChromeDurationFix = (videoEl) =>
  new Promise((resolve) => {
    if (!videoEl) {
      resolve();
      return;
    }

    const onMetadata = () => {
      videoEl.removeEventListener("loadedmetadata", onMetadata);

      if (videoEl.duration !== Infinity) {
        resolve();
        return;
      }

      // Force Chrome to compute the real duration
      // eslint-disable-next-line no-param-reassign
      videoEl.currentTime = Number.MAX_SAFE_INTEGER;
      const onTimeUpdate = () => {
        videoEl.removeEventListener("timeupdate", onTimeUpdate);
        // eslint-disable-next-line no-param-reassign
        videoEl.currentTime = 0;
        resolve();
      };
      videoEl.addEventListener("timeupdate", onTimeUpdate);
    };

    // If metadata is already loaded, run immediately
    if (videoEl.readyState >= 1 && videoEl.duration !== Infinity) {
      resolve();
    } else {
      videoEl.addEventListener("loadedmetadata", onMetadata);
    }
  });
